import React from 'react'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { Flex, Box } from 'reflexbox/styled-components'
import { kebabCase } from 'lodash'

import { PRICING_PLANS } from '@anvilco-pkg/sanity'

import PostHero from 'components/blog/PostHero'
import UniversalLink from 'components/UniversalLink'

import IconDynamicArrow from '@anvilco-pkg/ui/components/Icon/IconDynamicArrow'

import theme from 'theme'

import { FEATURE_SLUG } from 'consts'

import { isFeatureNew } from 'helpers/feature'

const StyledPostHero = styled(PostHero)`
  transition: transform 300ms ease 0s;
  transform: translateY(0px);
  position: relative;
  z-index: 0;
  height: 146px;
  width: 100%;
  overflow: hidden;
`

const TextContainer = styled(Flex).attrs({
  flexDirection: 'column',
  px: 24,
  pb: 24,
  pt: 20,
})`
  transition: all 300ms ease 0s;
  flex: 1;
  transform: translateY(40px);
  margin-top: -40px;
`

const ReadMoreLink = styled(Flex).attrs({
  alignItems: 'center',
})`
  opacity: 0;
  transition:
    opacity 1s ease 0s,
    background-color 1s ease 0s;
  margin-top: auto;
  padding-top: 20px;

  div {
    font-weight: ${theme.fontWeights.bold};
    margin-right: 10px;
  }
`

export const Container = styled(UniversalLink)`
  display: flex;
  flex-direction: column;
  position: relative;
  border: 2px solid ${theme.colors.blacks[10]};
  overflow: hidden;

  &&&& {
    color: ${theme.colors.black};
    text-decoration: none;

    &:hover {
      color: ${theme.colors.black};
      text-decoration: none;

      ${StyledPostHero} {
        transform: translateY(-32px);
      }

      ${TextContainer} {
        transform: translateY(0px);
        background-color: white;
      }

      ${ReadMoreLink} {
        opacity: 1;
      }
    }
  }
`

const Title = styled(Box).attrs({
  fontSize: 18,
  fontWeight: theme.fontWeights.bold,
  mb: 7,
})``

const Plan = styled(Box).attrs({
  color: theme.colors.greens[50],
  mb: 7,
  fontSize: 14,
  fontWeight: theme.fontWeights.normal,
})``

const Description = styled(Box).attrs({
  fontSize: 14,
  color: '#222222',
  fontWeight: theme.fontWeights.thin,
})``

const InfoContainer = styled(Flex).attrs({
  flexDirection: 'column',
  alignItems: 'flex-end',
})`
  position: absolute;
  z-index: 1;
  top: 5px;
  right: 5px;
  gap: 5px;
`

const Badge = styled(Box)
  .attrs({
    bg: theme.colors.black,
    lineHeight: 1.5,
    px: '6px',
    py: '1px',
    width: 'fit-content',
  })
  .withConfig({
    shouldForwardProp: (prop) => ['isLight'].indexOf(prop) === -1,
  })`
  border-radius: 100px;
  text-transform: uppercase;
  background-color: ${({ isLight }) => (isLight ? theme.colors.white : theme.colors.black)};
  color: ${({ isLight }) => (isLight ? theme.colors.black : theme.colors.white)};
  border: 1px solid ${({ isLight }) => (isLight ? theme.colors.blacks[10] : theme.colors.black)};
  font-size: ${({ isLight }) => (isLight ? '10px' : '15px')};
`

const FeatureSummary = ({ feature, tag, showPlanOnBadge = false }) => {
  const { name, description, coverImage, pricingPlan } = feature
  const isNew = isFeatureNew(feature)
  const PLAN =
    PRICING_PLANS.find((p) => p.key === pricingPlan?.[0])?.value ?? ''

  return (
    <Container
      to={`/products/${kebabCase(tag)}/${FEATURE_SLUG}/${kebabCase(name.toLowerCase())}/`}
    >
      <InfoContainer>
        {isNew && <Badge isLight={showPlanOnBadge}>🔥NEW</Badge>}
        {showPlanOnBadge && (
          <Badge isLight={showPlanOnBadge}>{pricingPlan?.[0] ?? ''}</Badge>
        )}
      </InfoContainer>
      <StyledPostHero image={coverImage.asset} title={name} />
      <TextContainer>
        <Title>{name}</Title>
        {!showPlanOnBadge && <Plan>{PLAN} plan</Plan>}
        <Description>{description}</Description>
        <ReadMoreLink>
          <div>Read more</div>
          <IconDynamicArrow direction="right" width={50} />
        </ReadMoreLink>
      </TextContainer>
    </Container>
  )
}

FeatureSummary.propTypes = {
  feature: PropTypes.object,
  tag: PropTypes.string,
  showPlanOnBadge: PropTypes.bool,
}

export default FeatureSummary
